import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle, ArrowLeft, Check, Clock, Copy, Download, ExternalLink, Loader2, Mail, RefreshCw, Send, ShieldCheck,
  Sparkles, Star, XCircle,
} from "lucide-react";
import PageHeader from "@/layout/PageHeader";
import RunInsightsCard from "@/components/RunInsightsCard";
import { SkeletonList } from "@/components/Skeleton";
import { API, authHeaders } from "@/lib/api";
import { fetchers, qk } from "@/lib/queryClient";
import { toast } from "sonner";
import DemiBrief, { McpBadge } from "@/pages/DemiBrief";

/**
 * CC-57 — EmailPreviewInline
 * Fetches the composed HTML from GET /api/daily-briefs/{id}/email-html
 * and drops it into a sandboxed iframe (srcDoc) with `allow-same-origin`
 * only (no `allow-scripts`). Used by the inline "Email preview" tab on
 * the brief detail page. VIEW ONLY — never triggers any send.
 */
function EmailPreviewInline({ briefId }) {
  const [html, setHtml] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setErr(null);
    (async () => {
      try {
        const r = await fetch(`${API}/daily-briefs/${briefId}/email-html`, {
          credentials: "include",
          headers: { ...authHeaders() },
        });
        if (!r.ok) {
          const t = await r.text().catch(() => "");
          throw new Error(`HTTP ${r.status} · ${t.slice(0, 160)}`);
        }
        const text = await r.text();
        if (!cancelled) setHtml(text);
      } catch (e) {
        if (!cancelled) setErr(e.message || "Failed to load email preview");
      }
    })();
    return () => { cancelled = true; };
  }, [briefId]);

  if (err) {
    return (
      <div
        className="hm-surface p-6 text-center"
        data-testid="email-preview-inline-error"
      >
        <XCircle className="h-6 w-6 text-hm-danger mx-auto mb-2" />
        <div className="text-sm text-hm-fg">Could not render email preview.</div>
        <p className="mono text-[11px] text-hm-fg-muted mt-1 break-all">{err}</p>
      </div>
    );
  }
  if (html === null) {
    return (
      <div
        className="hm-surface p-10 text-center"
        data-testid="email-preview-inline-loading"
      >
        <Loader2 className="h-6 w-6 text-hm-fg-subtle mx-auto mb-2 animate-spin" />
        <div className="mono text-[11px] uppercase tracking-widest text-hm-fg-subtle">
          Rendering email…
        </div>
      </div>
    );
  }
  return (
    <div
      className="hm-surface overflow-hidden"
      data-testid="email-preview-inline"
      style={{ background: "#F5F6F8" }}
    >
      <iframe
        title="Email preview"
        srcDoc={html}
        sandbox="allow-same-origin"
        data-testid="email-preview-iframe"
        className="w-full border-0"
        style={{ minHeight: 720, background: "#F5F6F8" }}
      />
    </div>
  );
}


const STATUS_ACTIONS = {
  draft: [
    { label: "Approve", next: "approved", icon: Check, primary: true },
    { label: "Mark for review", next: "review", icon: ShieldCheck },
  ],
  review: [
    { label: "Approve", next: "approved", icon: Check, primary: true },
    { label: "Back to draft", next: "draft", icon: ArrowLeft },
  ],
  approved: [
    { label: "Mark sent", next: "sent", icon: Send, primary: true },
    { label: "Back to review", next: "review", icon: ArrowLeft },
  ],
  completed: [
    { label: "Approve", next: "approved", icon: Check, primary: true },
    { label: "Mark for review", next: "review", icon: ShieldCheck },
    { label: "Back to draft", next: "draft", icon: ArrowLeft },
  ],
  empty: [
    { label: "Back to draft", next: "draft", icon: ArrowLeft },
  ],
  failed: [
    { label: "Back to draft", next: "draft", icon: RefreshCw },
  ],
  sent: [],
};

const REGION_LABEL = {
  global: "Global",
  americas: "Americas",
  asia: "Asia",
  europe: "Europe",
  middle_east: "Middle East",
  oceania: "Oceania",
};

const REGION_ORDER = ["global", "americas", "europe", "asia", "middle_east", "oceania"];

// -- Turn BV — pipeline_v2 canonical section order ------------------------
// Section labels come verbatim from `payload_v2.regions` keys (Wingman blueprint).
const V2_SECTION_ORDER = [
  "GLOBAL NEWS STORIES",
  "AMERICAS NEWS STORIES",
  "ASIA-PACIFIC NEWS STORIES",
  "EUROPE NEWS STORIES",
  "MIDDLE EAST & AFRICA NEWS STORIES",
  "OCEANIA NEWS STORIES",
  "TECHNOLOGY NEWS STORIES",
];

// How many top-priority articles surface in the "Global Highlights" hero.
// These are removed from the per-region sections to guarantee zero duplication.
const HIGHLIGHTS_COUNT = 5;
const HIGHLIGHTS_MIN_SCORE = 3;

function relativeDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.round(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.round(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

// Editorial markdown renderer for the daily-brief email preview.
// Follows the Beyond Activ format exactly:
//   • Bold uppercase regional headings (**GLOBAL NEWS STORIES**)
//   • Blue underlined external links that open in a new tab
//   • Container styled like a document, not a dashboard dump
const MD_P = ({ children, ...props }) => (
  <p {...props} style={{ margin: "0 0 6px 0", wordBreak: "break-word" }}>
    {children}
  </p>
);
const MD_STRONG = ({ children }) => (
  <strong
    style={{
      fontWeight: 700,
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      fontSize: "14px",
      display: "inline-block",
      marginTop: "24px",
      marginBottom: "8px",
      color: "var(--hm-fg)",
    }}
  >
    {children}
  </strong>
);
const MD_A = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="brief-source-link"
    style={{
      color: "var(--hm-accent)",
      textDecoration: "underline",
      textUnderlineOffset: "3px",
      wordBreak: "break-all",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.color = "var(--hm-accent-dim)";
      e.currentTarget.style.textDecoration = "none";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.color = "var(--hm-accent)";
      e.currentTarget.style.textDecoration = "underline";
    }}
  >
    {children}
  </a>
);
const MD_HR = () => (
  <hr
    style={{
      border: "none",
      borderTop: "1px solid var(--hm-border)",
      margin: "24px 0",
    }}
  />
);
const MD_H1 = ({ children }) => (
  <h1 style={{ fontSize: "20px", fontWeight: 600, margin: "24px 0 12px" }}>
    {children}
  </h1>
);
const MD_H2 = ({ children }) => (
  <h2 style={{ fontSize: "16px", fontWeight: 600, margin: "20px 0 8px" }}>
    {children}
  </h2>
);
const MD_UL = ({ children }) => (
  <ul style={{ paddingLeft: "18px", margin: "6px 0" }}>{children}</ul>
);
const MD_IMG = ({ src, alt }) => (
  <img
    src={src}
    alt={alt || ""}
    loading="lazy"
    data-testid="brief-story-image"
    style={{
      display: "block",
      width: "100%",
      aspectRatio: "16 / 9",
      objectFit: "cover",
      borderRadius: "8px",
      margin: "16px 0 10px 0",
      background: "var(--hm-bg-elev-2)",
    }}
    onError={(e) => {
      // If the rehosted image (or a still-external URL) fails to load,
      // gracefully hide the block so the story layout stays clean.
      e.currentTarget.style.display = "none";
    }}
  />
);
const MARKDOWN_COMPONENTS = {
  p: MD_P,
  strong: MD_STRONG,
  a: MD_A,
  hr: MD_HR,
  h1: MD_H1,
  h2: MD_H2,
  ul: MD_UL,
  img: MD_IMG,
};

function BriefMarkdown({ source }) {
  return (
    <div
      className="brief-editorial mx-auto"
      style={{
        maxWidth: "720px",
        background: "var(--hm-bg-elev-1)",
        border: "1px solid var(--hm-border)",
        borderRadius: "12px",
        padding: "32px",
        fontFamily:
          "Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        fontSize: "15px",
        lineHeight: "1.6",
        color: "var(--hm-fg)",
      }}
      data-testid="brief-editorial-render"
    >
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={MARKDOWN_COMPONENTS}>
        {source}
      </ReactMarkdown>
    </div>
  );
}

function PriorityBadge({ score }) {
  if (!score || score <= 0) return null;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
      style={{ color: "#0FA9DB", background: "#E6F4FB", border: "1px solid #B8E1F3" }}
      title={`Priority score ${score}`}
    >
      <Star className="h-2.5 w-2.5" strokeWidth={2.5} fill="#0FA9DB" />
      {score}
    </span>
  );
}

// Full article card — image + rewritten title + paragraph + meta row.
// This is the ONLY place an article is rendered on the brief detail page.
// The deep-link anchor #article-<id> lives on this element.
function ArticleCard({ a, compact = false }) {
  const hasImage = !!a.image_url;
  return (
    <article
      id={`article-${a.id}`}
      className="hm-flash-target"
      data-testid={`brief-article-${a.id}`}
      data-article-id={a.id}
      style={{
        background: "#fff",
        border: "1px solid #E5E9F0",
        borderRadius: 12,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {hasImage && (
        <div style={{ background: "#F7FAFC", aspectRatio: compact ? "16 / 9" : "16 / 8" }}>
          <img
            src={a.image_url}
            alt={a.image_alt || ""}
            loading="lazy"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
      )}
      <div style={{ padding: compact ? 16 : 18, display: "flex", flexDirection: "column", gap: 10 }}>
        <div className="flex items-start gap-2">
          <h3
            className="flex-1 min-w-0"
            style={{ color: "#0F1B2D", fontSize: compact ? 15 : 16, fontWeight: 600, lineHeight: 1.35 }}
          >
            {a.title_rewritten}
          </h3>
          <PriorityBadge score={a.priority_score} />
        </div>
        {a.paragraph_rewritten && (
          <p
            style={{
              color: "#3D4B62",
              fontSize: 13.5,
              lineHeight: 1.6,
              margin: 0,
              display: "-webkit-box",
              WebkitLineClamp: compact ? 3 : 5,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {a.paragraph_rewritten}
          </p>
        )}
        <div className="flex items-center gap-2 flex-wrap" style={{ fontSize: 11 }}>
          {a.source_name && (
            <span style={{ color: "#5B6B82", fontWeight: 500 }}>{a.source_name}</span>
          )}
          {a.published_at && (
            <>
              <span style={{ color: "#B8C1CE" }}>·</span>
              <span className="mono" style={{ color: "#5B6B82" }}>{relativeDate(a.published_at)}</span>
            </>
          )}
          {a.topic_tags?.slice(0, 3).map((t) => (
            <span
              key={t}
              className="mono"
              style={{
                background: "#F7FAFC", color: "#3D4B62",
                border: "1px solid #E5E9F0", borderRadius: 999,
                padding: "1px 8px", fontSize: 10,
                textTransform: "lowercase",
              }}
            >
              {t}
            </span>
          ))}
          {a.url && (
            <a
              href={a.url}
              target="_blank"
              rel="noreferrer"
              className="ml-auto inline-flex items-center gap-1 font-medium hover:underline"
              style={{ color: "#0FA9DB", fontSize: 11 }}
              data-testid={`brief-article-link-${a.id}`}
            >
              <ExternalLink className="h-3 w-3" />
              Source
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

// ------------------------------------------------------------------------
// Turn BV / BW / BX / BY — pipeline_v2 renderer.
// Editorial light-theme layout matching the Beyond Activ email screenshot :
// dark navy hero card, white document body, sky-blue accents, slate text.
// Images (Turn BW/BX) kept — same uniform size, now on a light backdrop.
// ------------------------------------------------------------------------
const _ABOVE_FOLD_EAGER = 2;

const HM_ACCENT_SKY = "#4FA8E0";
const HM_HERO_BG = "#1A2B4A";
const HM_TITLE_NAVY = "#0F1E36";

function V2Article({ article, priority = false }) {
  const imageUrl = article.image_url || null;
  const [imgLoaded, setImgLoaded] = useState(false);

  const companies = Array.isArray(article.companies) && article.companies.length
    ? article.companies.join(", ")
    : "TBD";
  const topics = Array.isArray(article.topics) && article.topics.length
    ? article.topics.join("; ")
    : "TBD";

  return (
    <article
      className="py-8 border-b border-slate-200 last:border-b-0"
      data-testid="brief-v2-article"
    >
      {/* 1. Title */}
      <h3 className="text-xl font-bold mb-4 leading-snug"
          style={{ color: HM_TITLE_NAVY }}>
        {article.source_url ? (
          <a
            href={article.source_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="brief-v2-article-title-link"
            className="transition-colors"
            style={{ color: "inherit" }}
            onMouseEnter={(e) => { e.currentTarget.style.color = HM_ACCENT_SKY; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = HM_TITLE_NAVY; }}
          >
            {article.title}
          </a>
        ) : (
          <span>{article.title}</span>
        )}
      </h3>

      {/* 2. Image between title and description */}
      {imageUrl && (
        <div className="mb-5 rounded-lg overflow-hidden border border-slate-200 relative h-56 bg-slate-100">
          <img
            src={imageUrl}
            alt=""
            width={800}
            height={450}
            loading={priority ? "eager" : "lazy"}
            fetchpriority={priority ? "high" : "auto"}
            decoding="async"
            data-testid="brief-v2-article-image"
            className="absolute inset-0 w-full h-56 object-cover block"
            style={{
              opacity: imgLoaded ? 1 : 0,
              transition: "opacity 220ms ease-out",
            }}
            onLoad={() => setImgLoaded(true)}
            onError={(e) => {
              const wrap = e.currentTarget.parentElement;
              if (wrap) wrap.style.display = "none";
            }}
          />
        </div>
      )}

      {/* 3. Description */}
      {article.summary && (
        <p className="text-[15px] text-slate-700 mb-5"
           style={{ lineHeight: 1.7 }}>
          {article.summary}
        </p>
      )}

      {/* 4. Meta line — Company · Topic · Source */}
      <div className="text-sm text-slate-600 flex flex-wrap items-center gap-x-3 gap-y-1"
           data-testid="brief-v2-article-meta">
        <span>
          <strong className="text-slate-900 font-semibold">Company:</strong>{" "}
          <span className="text-slate-600">{companies}</span>
        </span>
        <span className="text-slate-400">·</span>
        <span>
          <strong className="text-slate-900 font-semibold">Topic:</strong>{" "}
          <span className="text-slate-600">{topics}</span>
        </span>
        <span className="text-slate-400">·</span>
        <span>
          <strong className="text-slate-900 font-semibold">Source:</strong>{" "}
          {article.source_url ? (
            <a
              href={article.source_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="brief-v2-article-source-link"
              className="hover:underline"
              style={{ color: HM_ACCENT_SKY }}
            >
              {article.source_name || "Source"}
            </a>
          ) : (
            <span>{article.source_name || "TBD"}</span>
          )}
        </span>
      </div>
    </article>
  );
}

// ------------------------------------------------------------------------

// ------------------------------------------------------------------------
// Turn CC · Sub-Phase 4 — Delivery status line.
// Compact status ribbon replacing the old approve/regenerate/push toolbar.
// Reads send-history + delivery-settings to derive one of four states:
//   * delivered       — a `sent` row exists for auto or manual delivery.
//   * scheduled       — brief is today's target and 07:55 SGT hasn't passed.
//   * failed          — most recent send is `failed`.
//   * not_delivered   — past brief with no successful send.
// ------------------------------------------------------------------------
function DeliveryStatusLine({ briefId, briefDate, onOpenSend }) {
  /*
   * Turn CC · 16 — Migrated to react-query. All 3 dependencies
   * (send-history, delivery-settings, scheduler-status) are cached via
   * the persist client so the ribbon renders INSTANTLY on repeat visits.
   */
  const histQ = useQuery({
    queryKey: qk.dailyBriefSendHistory(briefId),
    queryFn: () => fetchers.dailyBriefSendHistory(briefId),
    staleTime: 60_000,
    refetchInterval: 30_000,
    enabled: !!briefId,
    retry: 1,
  });
  const settingsQ = useQuery({
    queryKey: qk.deliverySettings(),
    queryFn: fetchers.deliverySettings,
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const schedQ = useQuery({
    queryKey: qk.schedulerStatus(),
    queryFn: fetchers.schedulerStatus,
    staleTime: 30_000,
    retry: 1,
  });

  const _sgtYesterdayISO = () => {
    const nowUtc = new Date();
    const sgtOffsetMs = 8 * 60 * 60 * 1000;
    const sgtNow = new Date(nowUtc.getTime() + sgtOffsetMs);
    sgtNow.setUTCDate(sgtNow.getUTCDate() - 1);
    return sgtNow.toISOString().slice(0, 10);
  };

  const isLoading = histQ.isPending && !histQ.data;
  const hist = histQ.data?.sends || [];
  const settings = settingsQ.data;
  const scheduler = schedQ.data;

  let state;
  if (isLoading) {
    state = { kind: "loading" };
  } else {
    const lastSent = hist.find((h) => h.status === "sent");
    const lastFailed = hist.find((h) => h.status === "failed");
    if (lastSent) {
      state = {
        kind: "delivered",
        sent_at: lastSent.sent_at || lastSent.created_at,
        to: lastSent.to || [],
        trigger: lastSent.trigger || "manual",
      };
    } else {
      const nowSgt = new Date();
      const targetDateSgt = _sgtYesterdayISO();
      const isTodaysTarget = briefDate === targetDateSgt;
      const cronJob = (scheduler?.jobs || []).find((j) => j.id === "product1_auto_delivery");
      const nextRunUtc = cronJob?.next_run_utc;
      const beforeCron = nextRunUtc ? new Date(nextRunUtc) > nowSgt : false;
      const autoEnabled = !!settings?.auto_delivery_enabled;
      if (lastFailed) {
        state = { kind: "failed", created_at: lastFailed.created_at, error: lastFailed.error };
      } else if (isTodaysTarget && autoEnabled && beforeCron) {
        state = { kind: "scheduled", next_run_utc: nextRunUtc };
      } else {
        state = { kind: "not_delivered" };
      }
    }
  }

  if (state.kind === "loading") {
    return (
      <div className="mono text-[11px] uppercase tracking-widest text-hm-fg-subtle" data-testid="brief-delivery-status">
        checking delivery…
      </div>
    );
  }

  if (state.kind === "delivered") {
    return (
      <div
        className="flex items-center gap-2 text-xs text-hm-fg-muted"
        data-testid="brief-delivery-status"
        data-status="delivered"
      >
        <Check className="h-3.5 w-3.5 text-hm-accent" />
        <span>
          Delivered to <span className="mono text-hm-fg">{(state.to || []).join(", ")}</span>
          <span className="text-hm-fg-subtle mono text-[11px] ml-2">
            · {new Date(state.sent_at).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
            {" · "}
            {state.trigger === "auto_cron" ? "automated" : state.trigger === "retry" ? "retry" : "manual"}
          </span>
        </span>
      </div>
    );
  }

  if (state.kind === "scheduled") {
    const nextRun = new Date(state.next_run_utc);
    return (
      <div
        className="flex items-center gap-2 text-xs text-hm-fg-muted"
        data-testid="brief-delivery-status"
        data-status="scheduled"
      >
        <Clock className="h-3.5 w-3.5 text-hm-fg-subtle" />
        <span>
          Scheduled for delivery
          <span className="mono text-hm-fg-subtle ml-2">
            · {nextRun.toLocaleString(undefined, { hour: "2-digit", minute: "2-digit" })} SGT
          </span>
        </span>
      </div>
    );
  }

  if (state.kind === "failed") {
    return (
      <div
        className="flex items-center gap-2 text-xs text-hm-danger"
        data-testid="brief-delivery-status"
        data-status="failed"
      >
        <AlertCircle className="h-3.5 w-3.5" />
        <span>Delivery failed</span>
        <button
          onClick={onOpenSend}
          data-testid="brief-delivery-retry"
          className="ml-2 mono text-[11px] uppercase tracking-widest text-hm-accent hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  // not_delivered
  return (
    <div
      className="flex items-center gap-2 text-xs text-hm-fg-muted"
      data-testid="brief-delivery-status"
      data-status="not_delivered"
    >
      <Clock className="h-3.5 w-3.5 text-hm-fg-subtle" />
      <span>Not delivered</span>
      <button
        onClick={onOpenSend}
        data-testid="brief-delivery-send-now"
        className="ml-2 mono text-[11px] uppercase tracking-widest text-hm-accent hover:underline"
      >
        Send now
      </button>
    </div>
  );
}


// ------------------------------------------------------------------------
// Turn CC · Sub-Phase 4 — Enriched email preview panel.
// Wraps the raw preview iframe with a Copy HTML / Download .html / Send
// via Gmail toolbar so the operator can inspect and dispatch from a
// single surface.
// ------------------------------------------------------------------------
function EmailPreviewPanel({ open, onOpenChange, briefId, briefDate, onOpenSend }) {
  const [copying, setCopying] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const fetchHtml = async () => {
    const r = await fetch(`${API}/daily-brief-v2/${briefId}/preview-email`, {
      headers: authHeaders(),
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return await r.text();
  };

  const copyHtml = async () => {
    setCopying(true);
    try {
      const html = await fetchHtml();
      await navigator.clipboard.writeText(html);
      toast.success("HTML copied — paste into your email client.");
    } catch (e) {
      toast.error(`Copy failed: ${e.message}`);
    } finally {
      setCopying(false);
    }
  };

  const downloadHtml = async () => {
    setDownloading(true);
    try {
      const html = await fetchHtml();
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `beyond-activ-daily-brief-${briefDate || "latest"}.html`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("File downloaded.");
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    } finally {
      setDownloading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
      data-testid="preview-email-panel"
    >
      <div className="bg-hm-bg w-full max-w-4xl mx-4 rounded-xl border border-hm-border-strong shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 border-b border-hm-border flex items-center justify-between">
          <div>
            <div className="text-hm-fg font-semibold text-base">Email preview</div>
            <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mt-0.5">
              rendered from payload_v2 · exact recipient view
            </div>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            data-testid="preview-email-close"
            className="h-8 w-8 rounded-md text-hm-fg-subtle hover:text-hm-fg hover:bg-hm-bg-1"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 min-h-0 bg-slate-100 p-4 overflow-hidden">
          <iframe
            src={`${API}/daily-brief-v2/${briefId}/preview-email`}
            title="Email preview"
            data-testid="preview-email-iframe"
            className="w-full h-full rounded-md border border-hm-border bg-white"
          />
        </div>

        <div className="px-6 py-4 border-t border-hm-border flex items-center gap-2 flex-wrap">
          <button
            onClick={copyHtml}
            disabled={copying}
            data-testid="preview-email-copy"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs border border-hm-border text-hm-fg-muted hover:text-hm-fg hover:border-hm-border-strong disabled:opacity-40"
          >
            {copying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
            Copy HTML for email
          </button>
          <button
            onClick={downloadHtml}
            disabled={downloading}
            data-testid="preview-email-download"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs border border-hm-border text-hm-fg-muted hover:text-hm-fg hover:border-hm-border-strong disabled:opacity-40"
          >
            {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            Download .html
          </button>
          <button
            onClick={onOpenSend}
            data-testid="preview-email-send"
            className="ml-auto inline-flex items-center gap-1.5 h-9 px-4 rounded-md text-sm bg-hm-accent text-hm-bg font-medium hover:bg-hm-accent-dim"
          >
            <Send className="h-3.5 w-3.5" />
            Send via Gmail
          </button>
        </div>
      </div>
    </div>
  );
}


// Turn CC · Sub-Phase 3 — Send-via-Gmail modal.
// Renders an iframe of the composed HTML preview, lets the operator edit
// recipients + subject, and dispatches the send through the backend which
// owns the OAuth refresh + Gmail API call.
// ------------------------------------------------------------------------
function SendGmailModal({ open, onOpenChange, briefId }) {
  const [meIntegrations, setMeIntegrations] = useState(null);
  const [me, setMe] = useState(null);
  const [subject, setSubject] = useState("");
  const [toStr, setToStr] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const gmailCard = meIntegrations
    ? (meIntegrations.user_integrations || []).find((i) => i.provider === "gmail")
    : null;
  const connected = !!(gmailCard && gmailCard.status === "active");
  const senderEmail = gmailCard?.connected_email || null;

  const loadIntegrations = async () => {
    try {
      const r = await fetch(`${API}/me/integrations`, { headers: authHeaders() });
      if (r.ok) setMeIntegrations(await r.json());
    } catch {
      /* ignore */
    }
  };

  const loadMe = async () => {
    try {
      const r = await fetch(`${API}/auth/me`, { headers: authHeaders() });
      if (r.ok) setMe(await r.json());
    } catch {
      /* ignore */
    }
  };

  const loadHistory = async () => {
    try {
      const r = await fetch(`${API}/daily-brief-v2/${briefId}/send-history`, {
        headers: authHeaders(),
      });
      if (r.ok) {
        const body = await r.json();
        setHistory(body.sends || []);
      }
    } catch {
      /* ignore */
    } finally {
      setHistoryLoaded(true);
    }
  };

  useEffect(() => {
    if (!open) return;
    loadIntegrations();
    loadMe();
    loadHistory();
  }, [open, briefId]);

  // Fetch subject via a HEAD-style call: the preview endpoint returns full
  // HTML, and compose_subject lives backend-side. We derive a matching
  // default from the URL's brief_date instead — cheap and correct.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await fetch(`${API}/daily-briefs/${briefId}`, { headers: authHeaders() });
        if (!r.ok) return;
        const body = await r.json();
        const d = body.brief?.brief_date;
        if (!d) return;
        // Match backend `compose_subject` format:
        // "Beyond Activ Daily Intelligence — 22 September 2026"
        const dt = new Date(d + "T00:00:00Z");
        const pretty = dt.toLocaleDateString("en-GB", {
          day: "numeric", month: "long", year: "numeric", timeZone: "UTC",
        });
        setSubject(`Beyond Activ Daily Intelligence — ${pretty}`);
      } catch {
        /* ignore */
      }
    })();
  }, [open, briefId]);

  // Pre-fill recipients with the logged-in user's own email once known.
  useEffect(() => {
    if (open && me?.email && !toStr) setToStr(me.email);
  }, [open, me?.email]);

  const send = async () => {
    const cleaned = toStr
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (cleaned.length === 0) {
      toast.error("Add at least one recipient.");
      return;
    }
    const bad = cleaned.find((s) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s));
    if (bad) {
      toast.error(`Invalid email: ${bad}`);
      return;
    }
    setSending(true);
    try {
      const r = await fetch(`${API}/daily-brief-v2/${briefId}/send-email`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ to: cleaned, subject: subject || null }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        const code = body?.detail?.code || body?.detail || `HTTP ${r.status}`;
        if (code === "gmail_not_connected") {
          toast.error("Connect Gmail first — heading to Settings.");
          setTimeout(() => {
            window.location.assign("/settings?tab=integrations");
          }, 900);
          return;
        }
        if (code === "gmail_revoked") {
          toast.error("Gmail authorization was revoked — reconnect required.");
          setTimeout(() => {
            window.location.assign("/settings?tab=integrations");
          }, 900);
          return;
        }
        throw new Error(body?.detail?.reason || String(code));
      }
      toast.success(
        `Email sent to ${cleaned.length} recipient${cleaned.length === 1 ? "" : "s"}.`,
      );
      loadHistory();
    } catch (e) {
      toast.error(`Send failed: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
      data-testid="send-gmail-modal"
    >
      <div className="bg-hm-bg w-full max-w-3xl mx-4 rounded-xl border border-hm-border-strong shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 border-b border-hm-border flex items-center justify-between">
          <div>
            <div className="text-hm-fg font-semibold text-base">Send Daily Brief via Gmail</div>
            <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mt-0.5">
              rendered from payload_v2 · sent through your gmail
            </div>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            data-testid="send-gmail-close"
            className="h-8 w-8 rounded-md text-hm-fg-subtle hover:text-hm-fg hover:bg-hm-bg-1"
          >
            ✕
          </button>
        </div>

        {!connected ? (
          <div className="p-6" data-testid="send-gmail-not-connected">
            <div className="rounded-lg border border-hm-warn/40 bg-hm-warn/5 p-4 text-sm text-hm-fg">
              <div className="font-medium text-hm-warn mb-1">Gmail not connected</div>
              <p className="text-hm-fg-muted">
                You need to grant the <code className="mono">gmail.send</code> scope
                before you can dispatch a brief from your inbox. Head to Settings to connect.
              </p>
              <button
                onClick={() => window.location.assign("/settings?tab=integrations")}
                data-testid="send-gmail-cta-settings"
                className="mt-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-md text-xs bg-hm-accent text-hm-bg font-medium hover:bg-hm-accent-dim"
              >
                <Mail className="h-3.5 w-3.5" />
                Connect Gmail
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mb-1">
                  From
                </div>
                <div className="mono text-hm-fg" data-testid="send-gmail-from">{senderEmail}</div>
              </div>
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mb-1">
                  Brief
                </div>
                <div className="mono text-hm-fg-muted">{briefId?.slice(0, 8)}…</div>
              </div>
            </div>

            <div>
              <label className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle block mb-1">
                To (comma-separated)
              </label>
              <input
                value={toStr}
                onChange={(e) => setToStr(e.target.value)}
                placeholder="ops@beyondactiv.com, ..."
                data-testid="send-gmail-to"
                className="w-full h-9 px-3 rounded-md bg-hm-bg-1 border border-hm-border text-sm text-hm-fg focus:outline-none focus:border-hm-accent-dim"
              />
            </div>

            <div>
              <label className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle block mb-1">
                Subject
              </label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="send-gmail-subject"
                className="w-full h-9 px-3 rounded-md bg-hm-bg-1 border border-hm-border text-sm text-hm-fg focus:outline-none focus:border-hm-accent-dim"
              />
            </div>

            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mb-1">
                Preview
              </div>
              <iframe
                src={`${API}/daily-brief-v2/${briefId}/preview-email`}
                title="Email preview"
                data-testid="send-gmail-iframe"
                className="w-full h-[420px] rounded-md border border-hm-border bg-white"
              />
            </div>

            {historyLoaded && history.length > 0 && (
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mb-1">
                  Send history
                </div>
                <div className="rounded-md border border-hm-border divide-y divide-hm-border max-h-40 overflow-y-auto">
                  {history.map((h) => (
                    <div
                      key={h.id}
                      data-testid={`send-history-row-${h.id}`}
                      className="px-3 py-2 text-xs flex items-center gap-2"
                    >
                      <span
                        className={
                          "mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border " +
                          (h.status === "sent"
                            ? "border-hm-accent-dim text-hm-accent"
                            : h.status === "failed"
                              ? "border-hm-danger/40 text-hm-danger"
                              : "border-hm-warn/40 text-hm-warn")
                        }
                      >
                        {h.status}
                      </span>
                      <span className="mono text-hm-fg-muted">
                        {(h.to || []).join(", ")}
                      </span>
                      <span className="ml-auto mono text-[10px] text-hm-fg-subtle">
                        {h.sent_at
                          ? new Date(h.sent_at).toLocaleString()
                          : new Date(h.created_at).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="px-6 py-4 border-t border-hm-border flex items-center gap-3">
          <button
            onClick={() => onOpenChange(false)}
            data-testid="send-gmail-cancel"
            className="h-9 px-4 rounded-md text-xs border border-hm-border text-hm-fg-muted hover:text-hm-fg hover:border-hm-border-strong"
          >
            Cancel
          </button>
          <button
            onClick={send}
            disabled={!connected || sending}
            data-testid="send-gmail-submit"
            className="ml-auto inline-flex items-center gap-1.5 h-9 px-4 rounded-md text-sm bg-hm-accent text-hm-bg font-medium hover:bg-hm-accent-dim disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {sending ? (
              <>
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <Send className="h-3.5 w-3.5" />
                Send
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}



function V2Brief({ brief, payload }) {
  const regions = (payload && payload.regions) || {};
  const sections = V2SECTIONS_WITH_COUNTS(regions);
  const totalArticles = sections.reduce((sum, s) => sum + s.items.length, 0);
  const nonEmpty = sections.filter((s) => s.items.length > 0).length;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10 bg-white"
         data-testid="brief-v2-render">
      {/* Hero card — dark navy, sky-blue accents, centered */}
      <div className="rounded-xl px-12 py-10 text-center"
           style={{ background: HM_HERO_BG }}
           data-testid="brief-v2-hero">
        <div className="text-[11px] uppercase font-semibold mb-3"
             style={{ color: HM_ACCENT_SKY, letterSpacing: "0.25em" }}>
          Beyond Activ
        </div>
        <h1 className="text-4xl font-bold text-white mb-2 tracking-tight">
          Daily Brief
        </h1>
        <div className="text-lg font-medium mb-6"
             style={{ color: HM_ACCENT_SKY }}>
          {formatV2Date(brief.brief_date)}
        </div>
        <div className="inline-flex items-center px-6 py-2 rounded-full text-sm font-semibold"
             style={{
               background: "rgba(255,255,255,0.08)",
               color: HM_ACCENT_SKY,
               backdropFilter: "blur(4px)",
             }}
             data-testid="brief-v2-counter">
          {totalArticles} articles · {nonEmpty} sections
        </div>
      </div>

      {/* Sections */}
      {(() => {
        let eagerLeft = _ABOVE_FOLD_EAGER;
        return sections.map((section) => (
          <section key={section.key}
                   className="mt-12"
                   data-testid={`brief-v2-section-${section.key}`}>
            {/* Section header with left accent bar */}
            <div className="flex items-start gap-3 mb-8">
              <div className="w-1 h-14 rounded-sm shrink-0"
                   style={{ background: HM_ACCENT_SKY }} />
              <div>
                <h2 className="text-[15px] uppercase font-bold tracking-wider"
                    style={{ color: HM_ACCENT_SKY }}>
                  {section.key}
                </h2>
                <div className="text-sm text-slate-500 mt-1">
                  {section.items.length} article{section.items.length === 1 ? "" : "s"}
                </div>
              </div>
            </div>

            {section.items.length === 0 ? (
              <div className="py-6 text-sm italic text-slate-400"
                   data-testid={`brief-v2-section-empty-${section.key}`}>
                No stories today.
              </div>
            ) : (
              section.items.map((a, idx) => {
                const priority = eagerLeft > 0;
                if (priority) eagerLeft -= 1;
                return (
                  <V2Article
                    key={`${section.key}-${idx}`}
                    article={a}
                    priority={priority}
                  />
                );
              })
            )}
          </section>
        ));
      })()}
    </div>
  );
}

function V2SECTIONS_WITH_COUNTS(regions) {
  return V2_SECTION_ORDER.map((key) => ({
    key,
    items: Array.isArray(regions[key]) ? regions[key] : [],
  }));
}

function formatV2Date(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function DailyBriefDetail() {
  const { id } = useParams();
  const [sendOpen, setSendOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  // CC-57 — "Dashboard view ↔ Email preview" inline toggle (HM + Demi).
  const [viewMode, setViewMode] = useState("dashboard");

  /*
   * Turn CC · 16 — Brief detail is now driven by react-query so the
   * page paints INSTANTLY from the persist cache on repeat opens. The
   * detail is stable per-brief (once generated in `review` state it
   * doesn't change), so 5 min staleTime is safe.
   */
  const briefQ = useQuery({
    queryKey: qk.dailyBriefDetail(id),
    queryFn: () => fetchers.dailyBriefDetail(id),
    staleTime: 5 * 60_000,
    enabled: !!id,
  });
  const payload = briefQ.data;

  // Phase 7 — deep-link support: when arriving via `#article-<uuid>` from
  // the EntityDrawer's "See in brief" link, scroll the target article into
  // view and pulse the sky-blue halo (reuses the .hm-flash-highlight
  // keyframe used by the Companies hero jump-to).
  useEffect(() => {
    if (!payload?.articles?.length) return;
    const hash = window.location.hash;
    if (!hash || !hash.startsWith("#article-")) return;
    const targetId = hash.slice(1);
    const t = setTimeout(() => {
      const el = document.getElementById(targetId);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("hm-flash-highlight");
      setTimeout(() => el.classList.remove("hm-flash-highlight"), 1700);
    }, 120);
    return () => clearTimeout(t);
  }, [payload?.articles]);

  // Phase 7 — unified rendering: choose top-N by priority_score as
  // "Global Highlights" and EXCLUDE them from per-region sections so
  // each article appears exactly once in the DOM.
  // Placed BEFORE the early-return so hook order stays stable.
  const { highlights, groupedByRegion } = useMemo(() => {
    // Defensive dedup: even if the API accidentally returns the same
    // article twice, we render it exactly once. Preserves order.
    const seenIds = new Set();
    const list = (payload?.articles || []).filter((a) => {
      if (!a?.id || seenIds.has(a.id)) return false;
      seenIds.add(a.id);
      return true;
    });
    const sorted = [...list].sort((a, b) => {
      const s = (b.priority_score || 0) - (a.priority_score || 0);
      if (s !== 0) return s;
      const pb = b.published_at ? new Date(b.published_at).getTime() : 0;
      const pa = a.published_at ? new Date(a.published_at).getTime() : 0;
      return pb - pa;
    });
    const hl = sorted
      .filter((a) => (a.priority_score || 0) >= HIGHLIGHTS_MIN_SCORE)
      .slice(0, HIGHLIGHTS_COUNT);
    const hlIds = new Set(hl.map((a) => a.id));
    const rest = list.filter((a) => !hlIds.has(a.id));
    const buckets = {};
    for (const a of rest) {
      const r = a.region || "global";
      (buckets[r] = buckets[r] || []).push(a);
    }
    for (const r of Object.keys(buckets)) {
      buckets[r].sort((a, b) => {
        const s = (b.priority_score || 0) - (a.priority_score || 0);
        if (s !== 0) return s;
        const pb = b.published_at ? new Date(b.published_at).getTime() : 0;
        const pa = a.published_at ? new Date(a.published_at).getTime() : 0;
        return pb - pa;
      });
    }
    return { highlights: hl, groupedByRegion: buckets };
  }, [payload?.articles]);

  if (!payload) {
    return (
      <div>
        <PageHeader title="Daily brief" subtitle="Loading…" />
        <SkeletonList n={4} cols={3} />
      </div>
    );
  }

  const { brief, articles } = payload;

  /*
   * Turn CC · 13 — Legacy v1 mutation handlers removed:
   *   transition() / regenerate() / push() / saveNotes()
   * All hit endpoints (`POST /status`, `/regenerate`, `/push`,
   * `PATCH /notes`) that no longer exist. The page is now read-only for
   * both v1 legacy briefs and v2 briefs. v2 delivery (Preview email +
   * Send to Gmail) remains — those go through /api/daily-brief-v2/*.
   */

  const canPush = brief.status === "approved";

  // Turn BV — pipeline_v2 detection. Drives the render branch below.
  const isV2 = brief.pipeline_version === "v2" && !!brief.payload_v2;
  // CC-57 — Demi briefs pushed via Claude Pro MCP.
  const isDemi = (brief.pipeline_version || "").startsWith("claude_pro_mcp")
    && !!brief.payload_v2;
  const v2TotalArticles = isV2
    ? V2SECTIONS_WITH_COUNTS(brief.payload_v2.regions || {}).reduce(
        (s, sec) => s + sec.items.length, 0)
    : 0;
  const v2NonEmptySections = isV2
    ? V2SECTIONS_WITH_COUNTS(brief.payload_v2.regions || {}).filter(
        (s) => s.items.length > 0).length
    : 0;
  const demiSegmentCount = isDemi
    ? (brief.payload_v2?.imported_raw?.segments?.length || 0)
    : 0;

  // CC-57 — inline "Dashboard view ↔ Email preview" toggle (HM + Demi).
  const emailPreviewSupported = isV2 || isDemi;

  return (
    <div data-testid="brief-detail-page">
      <PageHeader
        title={`Daily brief · ${brief.brief_date}`}
        badge={brief.status}
        subtitle={
          isDemi
            ? `${demiSegmentCount} segment(s) · pushed via Claude Pro MCP`
            : isV2
              ? `${v2TotalArticles} article(s) across ${v2NonEmptySections} section(s) · pipeline v2`
              : `${brief.article_count} article(s) across ${Object.keys(brief.region_stats || {}).length} region(s).`
        }
      />

      <div className="mb-4 flex items-center justify-between">
        <Link
          to="/daily-briefs"
          className="inline-flex items-center gap-1.5 text-xs text-hm-fg-muted hover:text-hm-fg"
          data-testid="brief-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          All briefs
        </Link>
        {isDemi && <McpBadge data-testid="demi-brief-header-badge" />}
      </div>

      {emailPreviewSupported && (
        <div
          role="tablist"
          aria-label="View mode"
          data-testid="brief-view-mode-toggle"
          className="inline-flex items-center gap-1 rounded-lg border border-hm-border bg-hm-bg-1 p-1 mb-4"
        >
          {[
            { id: "dashboard", label: "Dashboard view" },
            { id: "email",     label: "Email preview" },
          ].map((m) => {
            const active = viewMode === m.id;
            return (
              <button
                key={m.id}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`brief-view-mode-${m.id}`}
                onClick={() => setViewMode(m.id)}
                className={`mono uppercase tracking-widest text-[11px] px-3 py-1.5 rounded-md transition-colors ${
                  active
                    ? "bg-hm-accent-soft text-hm-accent-dim"
                    : "text-hm-fg-muted hover:text-hm-fg"
                }`}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      )}

      {brief.failure_reason && (
        <div className="hm-surface border-hm-danger/40 p-4 mb-4 flex items-start gap-3">
          <XCircle className="h-5 w-5 text-hm-danger shrink-0 mt-0.5" />
          <div>
            <div className="text-sm text-hm-danger font-medium">Generation failed</div>
            <p className="mono text-[11px] text-hm-fg-muted mt-1 break-all">
              {brief.failure_reason}
            </p>
          </div>
        </div>
      )}

      {!isV2 && !isDemi && (
        <div
          className="hm-surface p-4 mb-4 flex items-start gap-3"
          data-testid="legacy-brief-banner"
          style={{ borderColor: "var(--hm-border)" }}
        >
          <Clock className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "#B45309" }} />
          <div>
            <div className="text-sm text-hm-fg font-medium">
              Legacy v1 brief · read-only
            </div>
            <p className="mono text-[11px] text-hm-fg-muted mt-1">
              This brief was generated by the deprecated v1 pipeline
              (superseded by pipeline_v2 in Turn CC · 6c). Displayed for
              historical audit; no actions are available. All new briefs
              use pipeline_v2 · <code>/api/daily-brief-v2/*</code>.
            </p>
          </div>
        </div>
      )}

      {isV2 && (
        <div className="hm-surface p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="brief-actions">
          <DeliveryStatusLine
            briefId={id}
            briefDate={brief.brief_date}
            onOpenSend={() => setPreviewOpen(true)}
          />
          <button
            onClick={() => setPreviewOpen(true)}
            data-testid="brief-preview-email"
            className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs border border-hm-border text-hm-fg-muted hover:text-hm-fg hover:border-hm-border-strong"
          >
            <Mail className="h-3.5 w-3.5" />
            Preview email
          </button>
        </div>
      )}

      <EmailPreviewPanel
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        briefId={id}
        briefDate={brief.brief_date}
        onOpenSend={() => {
          setPreviewOpen(false);
          setSendOpen(true);
        }}
      />

      <SendGmailModal
        open={sendOpen}
        onOpenChange={setSendOpen}
        briefId={id}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-8">
          {viewMode === "email" && emailPreviewSupported ? (
            <EmailPreviewInline briefId={id} />
          ) : isDemi ? (
            <DemiBrief payload={brief.payload_v2} />
          ) : isV2 ? (
            <V2Brief brief={brief} payload={brief.payload_v2} />
          ) : articles.length === 0 ? (
            <div className="hm-surface p-8 text-center mono text-[11px] uppercase tracking-widest text-hm-fg-subtle">
              No articles yet. Generation may still be in progress.
            </div>
          ) : (
            <>
              {/* Global Highlights — top-N by priority_score. Removed from
                  region sections so each article renders exactly once. */}
              {highlights.length > 0 && (
                <section data-testid="brief-highlights">
                  <div className="flex items-baseline gap-2 mb-4 pb-2 border-b" style={{ borderColor: "#E5E9F0" }}>
                    <Star className="h-5 w-5" strokeWidth={2.5} fill="#0FA9DB" color="#0FA9DB" />
                    <h2 style={{ color: "#0F1B2D", fontSize: 20, fontWeight: 600 }}>
                      Global Highlights
                    </h2>
                    <span className="mono text-[11px] uppercase tracking-widest" style={{ color: "#5B6B82" }}>
                      ({highlights.length})
                    </span>
                  </div>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    {highlights.map((a) => (
                      <ArticleCard key={a.id} a={a} compact />
                    ))}
                  </div>
                </section>
              )}

              {/* Region sections — ordered Global → Americas → Europe → Asia
                  → Middle East → Oceania. Each article ONLY here if it was
                  not already surfaced in Highlights. */}
              {REGION_ORDER.filter((r) => groupedByRegion[r]?.length).map((region) => {
                const list = groupedByRegion[region];
                return (
                  <section key={region} data-testid={`brief-region-${region}`}>
                    <div className="flex items-baseline gap-2 mb-4 pb-2 border-b" style={{ borderColor: "#E5E9F0" }}>
                      <h2 style={{ color: "#0F1B2D", fontSize: 20, fontWeight: 600 }}>
                        {REGION_LABEL[region] || region}
                      </h2>
                      <span className="mono text-[11px] uppercase tracking-widest" style={{ color: "#5B6B82" }}>
                        ({list.length})
                      </span>
                    </div>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      {list.map((a) => (
                        <ArticleCard key={a.id} a={a} />
                      ))}
                    </div>
                  </section>
                );
              })}
            </>
          )}
        </div>

        <aside className="h-fit" data-testid="brief-sidebar">
          {isV2 && !isDemi && <RunInsightsCard briefId={brief?.id} />}
          {brief.notes && (
            <div className="hm-surface p-4" data-testid="brief-notes">
              <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mb-2">
                Editorial notes
              </div>
              <div
                className="text-sm text-hm-fg leading-relaxed whitespace-pre-wrap"
                data-testid="brief-notes-display"
              >
                {brief.notes}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
